import * as React from 'react';

import styled from '@emotion/styled';

import { Box, COLORS, Text, mq } from '@clutter/clean';

import { ZipCTA } from '@root/components/www/home/zip_cta';

import { ZIP_INPUT_TRIGGER_ID } from '@shared/header/header';
import { ImageDescriptor } from '@shared/page_builder/types/image_descriptor';
import { PinnedFooterContainer } from '@shared/pinned_footer_container';
import { ResponsiveImage } from '@shared/responsive_image';
import { ServiceSelection } from '@shared/service_selector/constants';
import { HeroServiceSelector } from '@shared/service_selector/hero_service_selector';
import { PinnedServiceSelector } from '@shared/service_selector/pinned_service_selector';

import { useIntersectionObserver } from '@utils/hooks';

const HERO_ZIP_INPUT_ID = 'hero_zip_input';
const SINGLE_IMAGE_ASPECT_RATIO = 0.518;

/**
 * Minimizes layout shift by calculating the height of the hero image with respect to this component, via:
 * 1. Use the min of the calculated value and 472px (the max possible height given a width of 1600px)
 * 2. Calculate the max available space as half of of the browser width minus 60px padding on each side
 * 3. Multiply by the required aspect ratio of the image
 * 4. Add 60px padding to match
 */
const SINGLE_IMAGE_HERO_HEIGHT = `min(calc(((100vw/2) - 60px) * ${SINGLE_IMAGE_ASPECT_RATIO} + 120px), 472px)`;

const Container = styled.div`
  color: ${COLORS.tealDark};
  overflow: hidden;

  ${mq({
    paddingTop: ['64px', null, 0],
  })}
`;

const PinnedFooter = styled(PinnedFooterContainer)`
  ${mq({
    display: ['flex', 'none'],
  })}
  justify-content: center;
  form {
    width: 100%;
  }
`;

const Title = styled(Text.Display)`
  margin-bottom: 24px;

  ${mq({
    maxHeight: [null, null, '216px'],
  })}
`;

const Subtitle = styled(Text.Headline)`
  ${mq({ marginBottom: ['32px', null, '48px'] })}
`;

type Alignment = 'image-then-hero' | 'hero-then-image';

const SingleHeroImage = styled(ResponsiveImage)`
  border-radius: 8px;
  max-height: 352px;
  width: unset;
  height: auto;
`;

const CONTAINER_PADDING_FOR_ALIGNMENT = {
  'image-then-hero': ['0 24px', null, '60px 60px 60px 20px'],
  'hero-then-image': ['0 24px', null, '60px 20px 60px 60px'],
};

const HERO_ORDER_FOR_ALIGNMENT = {
  'image-then-hero': [0, null, 1],
  'hero-then-image': [0, null, 0],
};

const IMAGE_ORDER_FOR_ALIGNMENT = {
  'image-then-hero': [1, null, 0],
  'hero-then-image': [1, null, 1],
};

export const ImageHero: React.FC<{
  /** The largest displayed text */
  title?: React.ReactNode;
  /** Optional copy displayed directly under the title */
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  image: ImageDescriptor;
  imageAlt: string;
  alignment?: Alignment;
  /** The service which will be used when submitting the zip form. If omitted, the service selector will be shown. */
  service?: ServiceSelection;
  /** The initial service if a service selector is present. */
  initialService?: ServiceSelection;
}> = ({
  title,
  subtitle,
  children,
  image,
  imageAlt,
  alignment = 'image-then-hero',
  service,
  initialService,
}) => {
  const [setNode, entry] = useIntersectionObserver();
  const isIntersecting = entry ? entry.isIntersecting : true;

  return (
    <Container ref={setNode}>
      <Box.Flex
        maxWidth="1600px"
        alignItems={['normal', null, 'center']}
        margin="0 auto"
        gap={['40px', null, null, '80px']}
        flexDirection={['column', null, 'row']}
        padding={CONTAINER_PADDING_FOR_ALIGNMENT[alignment]}
        minHeight={[null, null, SINGLE_IMAGE_HERO_HEIGHT]}
      >
        <Box.Flex
          order={IMAGE_ORDER_FOR_ALIGNMENT[alignment]}
          flexGrow={1}
          flexShrink={1}
          flexBasis={[null, null, '40%', '50%']}
          flexDirection="row"
          justifyContent="center"
        >
          <SingleHeroImage image={image} alt={imageAlt} loading="eager" />
        </Box.Flex>

        <Box.Flex
          order={HERO_ORDER_FOR_ALIGNMENT[alignment]}
          flexGrow={0}
          flexShrink={0}
          flexBasis={[null, null, '60%', '50%']}
          flexDirection="column"
          justifyContent="center"
        >
          <div>
            {title && <Title>{title}</Title>}
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
            {children}
          </div>
          <div
            id={ZIP_INPUT_TRIGGER_ID}
            data-test-id="landing_page_cta_container"
          >
            {service ? (
              <ZipCTA
                id="hero_zip_cta"
                wtConfig={{ container: 'product_hero', service }}
                service={service}
                inputId={HERO_ZIP_INPUT_ID}
              />
            ) : (
              <HeroServiceSelector
                id="hero_service_selector"
                initialService={initialService}
              />
            )}
          </div>
        </Box.Flex>
        <PinnedFooter show={!isIntersecting} hideMobile={true}>
          {service ? (
            <ZipCTA
              id="footer_zip_cta"
              wtConfig={{ container: 'pin_zip', service }}
              wrap={false}
              inputPlaceholder="Enter your ZIP"
              service={service}
            />
          ) : (
            <PinnedServiceSelector
              position="footer"
              id="footer_service_selector"
              container="landing_page_footer_service_selector"
            />
          )}
        </PinnedFooter>
      </Box.Flex>
    </Container>
  );
};
